import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { API_BASE, USE_CASES } from "../data/brand";
import { useLocations } from "../hooks/useLocations";

const schema = z.object({
  name: z.string().min(1, "Please enter your name").max(120),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(7, "Please enter a phone number").max(40),
  location_slug: z.string().min(1, "Please choose a location"),
  event_date: z.string().min(1, "Please choose a date"),
  guests: z.coerce
    .number({ invalid_type_error: "How many guests?" })
    .int()
    .min(1, "At least 1 guest")
    .max(1000, "Please contact us directly for very large events"),
  event_type: z.string().min(1, "Please choose an event type"),
  message: z.string().max(2000, "Please keep it under 2000 characters").optional().or(z.literal("")),
});

const Err = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-1.5 text-xs text-[#C6300F]">
      {message}
    </p>
  ) : null;

export default function InquiryForm() {
  const { data: locations = [] } = useLocations();
  const [status, setStatus] = useState("idle");
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      location_slug: "",
      event_date: "",
      guests: "",
      event_type: "",
      message: "",
    },
  });

  const name = watch("name");
  const today = new Date().toISOString().slice(0, 10);

  const onSubmit = async (values) => {
    setStatus("sending");
    try {
      await axios.post(`${API_BASE}/group-inquiries`, values);
      reset();
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div
        data-testid="form-success"
        role="status"
        className="rounded-2xl border border-bv-flame/40 bg-bv-flame/10 p-8 text-center md:p-10"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-bv-flame" aria-hidden="true" />
        <h3 className="mt-4 font-display text-3xl uppercase text-bv-ink">Inquiry sent.</h3>
        <p className="mt-2 text-sm leading-relaxed text-bv-sub">
          Thanks{name ? `, ${name.split(" ")[0]}` : ""} — our team will get back to you at the email
          you provided.
        </p>
        <button type="button" className="btn-ghost mt-6" onClick={() => setStatus("idle")} data-testid="form-success-again">
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid="groups-form"
      className="rounded-2xl border border-bv-line bg-bv-panel p-6 md:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="inq-name" className="field-label">Name</label>
          <input id="inq-name" type="text" autoComplete="name" className="field-input" placeholder="Your name"
            aria-invalid={!!errors.name} aria-describedby={errors.name ? "err-name" : undefined}
            data-testid="form-name" {...register("name")} />
          <Err id="err-name" message={errors.name?.message} />
        </div>

        <div>
          <label htmlFor="inq-email" className="field-label">Email</label>
          <input id="inq-email" type="email" autoComplete="email" className="field-input" placeholder="you@email.com"
            aria-invalid={!!errors.email} aria-describedby={errors.email ? "err-email" : undefined}
            data-testid="form-email" {...register("email")} />
          <Err id="err-email" message={errors.email?.message} />
        </div>

        <div>
          <label htmlFor="inq-phone" className="field-label">Phone</label>
          <input id="inq-phone" type="tel" autoComplete="tel" className="field-input" placeholder="(555) 555-0100"
            aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "err-phone" : undefined}
            data-testid="form-phone" {...register("phone")} />
          <Err id="err-phone" message={errors.phone?.message} />
        </div>

        <div>
          <label htmlFor="inq-location" className="field-label">Preferred Barverse Location</label>
          <select id="inq-location" className="field-input" aria-invalid={!!errors.location_slug}
            aria-describedby={errors.location_slug ? "err-location" : undefined}
            data-testid="form-location" {...register("location_slug")}>
            <option value="">Select a location</option>
            {locations.map((l) => (
              <option key={l.slug} value={l.slug}>
                {l.name}{l.is_demo ? " (demo)" : ""}
              </option>
            ))}
            <option value="unsure">Not sure yet</option>
            {locations.length === 0 && <option value="other">Other / not listed yet</option>}
          </select>
          <Err id="err-location" message={errors.location_slug?.message} />
        </div>

        <div>
          <label htmlFor="inq-date" className="field-label">Event Date</label>
          <input id="inq-date" type="date" min={today} className="field-input"
            aria-invalid={!!errors.event_date} aria-describedby={errors.event_date ? "err-date" : undefined}
            data-testid="form-date" {...register("event_date")} />
          <Err id="err-date" message={errors.event_date?.message} />
        </div>

        <div>
          <label htmlFor="inq-guests" className="field-label">Estimated Number of Guests</label>
          <input id="inq-guests" type="number" min="1" max="1000" className="field-input" placeholder="e.g. 12"
            aria-invalid={!!errors.guests} aria-describedby={errors.guests ? "err-guests" : undefined}
            data-testid="form-guests" {...register("guests")} />
          <Err id="err-guests" message={errors.guests?.message} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="inq-type" className="field-label">Event Type</label>
          <select id="inq-type" className="field-input" aria-invalid={!!errors.event_type}
            aria-describedby={errors.event_type ? "err-type" : undefined}
            data-testid="form-type" {...register("event_type")}>
            <option value="">Choose an event type</option>
            {USE_CASES.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <Err id="err-type" message={errors.event_type?.message} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="inq-message" className="field-label">Message (optional)</label>
          <textarea id="inq-message" rows="4" className="field-input resize-y"
            placeholder="Tell us about your crew and what you're planning."
            aria-invalid={!!errors.message} aria-describedby={errors.message ? "err-message" : undefined}
            data-testid="form-message" {...register("message")} />
          <Err id="err-message" message={errors.message?.message} />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button type="submit" className="btn-primary w-full sm:w-auto" disabled={status === "sending"} data-testid="form-submit">
          {status === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
            </>
          ) : (
            "Send Inquiry"
          )}
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-bv-faint">
          No booking yet — our team follows up personally.
        </p>
      </div>

      {status === "error" && (
        <p role="alert" className="mt-4 rounded-lg border border-[#C6300F]/40 bg-[#C6300F]/10 px-4 py-3 text-sm text-[#C6300F]" data-testid="form-error">
          Something went wrong sending your inquiry. Please try again, or email us directly.
        </p>
      )}
    </form>
  );
}
